import {
  LearningCenterCategory,
  LearningCenterCategoryNavItem,
  LearningCenterPost,
  LearningCenterPostSummary,
} from "./types";
import { typedQuery } from "../../../typed-query";

/**
 * @returns all live learnPost items, ordered by date descending
 */
export const allLearningCenterPostsQuery = typedQuery<
  LearningCenterPostSummary[]
>(`
*[_type == "intercomComLearnPost" && isLive == true] | order(coalesce(publishedDate, _createdAt) desc) {
  _id,
  _createdAt,
  _updatedAt,
  publishedDate,
  updatedDate,
  title,
  "slug": slug.current,
  isLive,
  excerpt,
  "seoMetadata": seoMetadata{ metadataDescription }
}`);

/**
 * @returns a single intercomComLearnPost by slug with full body content
 */
export const learningCenterPostBySlugQuery = typedQuery<LearningCenterPost>(`
*[_type == "intercomComLearnPost" && slug.current == $slug][0] {
  _id,
  "category": category->{ title, "slug": slug.current },
  "relatedSameCategory": *[_type == "intercomComLearnPost" && isLive == true && _id != ^._id && "drafts." + _id != ^._id && !(_id in ^.body[_type == "learnRelatedArticlesBlock"].items[]._ref) && defined(^.category._ref) && category._ref == ^.category._ref] | order(coalesce(publishedDate, _createdAt) desc) [0...12] { _id, title, "slug": slug.current },
  "relatedLatest": *[_type == "intercomComLearnPost" && isLive == true && _id != ^._id && "drafts." + _id != ^._id && !(_id in ^.body[_type == "learnRelatedArticlesBlock"].items[]._ref)] | order(coalesce(publishedDate, _createdAt) desc) [0...12] { _id, title, "slug": slug.current },
  _type,
  _createdAt,
  _updatedAt,
  publishedDate,
  updatedDate,
  title,
  "slug": slug.current,
  isLive,
  breadcrumbTitle,
  body[]{
    ...,
    _type == "image" => {
      ...,
      "asset": asset->{ url }
    },
    _type == "learnRelatedArticlesBlock" => {
      ...,
      items[@->isLive == true]{ _key, ...@->{ _id, _type, title, "slug": slug.current } }
    }
  },
  author,
  "mainImage": mainImage{ alt, "asset": asset->{ url } },
  seoMetadata{
    metadataTitle,
    metadataDescription,
    "socialImage": socialImage{ "asset": asset->{ url } },
    jsonLdSchemas
  }
}`);

/**
 * @returns a paginated slice of live intercomComLearnPosts
 */
export const paginatedLearningCenterPostsQuery = typedQuery<
  LearningCenterPostSummary[]
>(`
*[_type == "intercomComLearnPost" && isLive == true] | order(coalesce(publishedDate, _createdAt) desc) [$start...$end] {
  _id,
  _createdAt,
  publishedDate,
  updatedDate,
  title,
  "slug": slug.current,
  isLive,
  excerpt,
  "seoMetadata": seoMetadata{ metadataDescription }
}`);

/**
 * @returns the total count of live intercomComLearnPosts
 */
export const learningCenterPostsCountQuery = typedQuery<number>(`
count(*[_type == "intercomComLearnPost" && isLive == true])
`);

/**
 * @returns slugs of all live intercomComLearnPosts (for static generation)
 */
export const allLearningCenterPostSlugsQuery = typedQuery<
  Pick<
    LearningCenterPost,
    "slug" | "_createdAt" | "publishedDate" | "updatedDate"
  >[]
>(`
*[_type == "intercomComLearnPost" && isLive == true] {
  "slug": slug.current,
  _createdAt,
  publishedDate,
  updatedDate
}`);

const livePostsInCategory = `_type == "intercomComLearnPost" && isLive == true && category._ref == ^._id`;

/**
 * @returns categories with at least one live intercomComLearnPost, in editor order
 */
export const learningCenterCategoriesWithPostsQuery = typedQuery<
  LearningCenterCategoryNavItem[]
>(`
*[_type == "learnCategory" && defined(slug.current) && count(*[${livePostsInCategory}]) > 0] | order(order asc) {
  _id,
  title,
  "slug": slug.current,
  "postCount": count(*[${livePostsInCategory}])
}`);

/**
 * @returns a single learnCategory by slug with its intercom.com copy
 */
export const learningCenterCategoryBySlugQuery =
  typedQuery<LearningCenterCategory | null>(`
*[_type == "learnCategory" && slug.current == $slug][0] {
  _id,
  title,
  "slug": slug.current,
  "intro": intercomCom.intro,
  "seoTitle": intercomCom.seoTitle,
  "seoDescription": intercomCom.seoDescription,
  "postCount": count(*[${livePostsInCategory}])
}`);

/**
 * @returns a paginated slice of live intercomComLearnPosts in one category
 */
export const paginatedLearningCenterPostsByCategoryQuery = typedQuery<
  LearningCenterPostSummary[]
>(`
*[_type == "intercomComLearnPost" && isLive == true && category._ref == $categoryId] | order(coalesce(publishedDate, _createdAt) desc) [$start...$end] {
  _id,
  _createdAt,
  publishedDate,
  updatedDate,
  title,
  "slug": slug.current,
  isLive,
  excerpt,
  "seoMetadata": seoMetadata{ metadataDescription }
}`);
